import React from "react"
import { styled, keyframes } from "@mui/material/styles"
import { css } from '@emotion/react';
import { Fade, LinearProgress } from "@mui/material"
import { observer, inject } from "mobx-react"
import { getVolume, stopAllAudioExceptSlug } from "../utils/userUtils"
import AudioPlayer from "./AudioPlayer"

const shadowSlide = keyframes`
  0% { width: 0; }
  100% { width: 100%; }
`

const CardPlayShadow = styled('div')(({ duration }) => css`
  height: 100%;
  width: 0;
  position: absolute;
  top: 0;
  left: 0;
  opacity: 0.08;
  z-index: 50;
  background-color: #000;
  border-radius: 5px 0 0 0;
  animation: ${shadowSlide} ${duration}s linear infinite;
`);

const SequenceWaveformContainer = styled('div')`
  cursor: pointer;
  position: relative;
  height: 100%;
`

const CardImageContainer = styled('div')`
  position: relative;
  height: 100%;
`

const CardImage = styled('img')(({ assignedColor }) => css`
  height: 100%;
  width: 100%;
  border-radius: 5px;
  background-color: ${assignedColor} !important;
`)

const WaveformTitle = styled('div')`
  position: absolute;
  bottom: 0;
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.7) 30%,
    rgba(255, 255, 255, 1) 100%
  );
  padding: 10px 20px;
  font-size: 20px;
  font-weight: 300;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  color: black;
  box-sizing: border-box;
`

const SFLinearProgress = styled(LinearProgress)`
  border-radius: 5px 5px 0 0;
  position: absolute;
  width: 100%;
`
// In order to optionally check if certain stores are injected
// https://github.com/mobxjs/mobx-react/issues/232
@inject((props, stores, context) => props)
@observer
export default class SequenceWaveform extends React.Component {
  constructor(props) {
    super(props)
    const volume = getVolume()

    this.state = {
      isPlaying: false,
      isLoading: false,
      isLoaded: false,
      shouldLoop: true,
      volumeValue: volume,
    }

    this.audioPlayer = React.createRef()
  }

  static defaultProps = {
    displayTitle: true,
    shouldRenderTimestampsIfMod: true,
  }

  componentDidMount() {
    if (typeof(window) !== "undefined") {
      window.addEventListener("volumeChanged", this.setVolume)
      window.addEventListener("stopAllAudioExcept", this.stopAudioExcept)
    }
  }

  componentWillUnmount() {
    if (typeof(window) !== "undefined") {
      window.removeEventListener("volumeChanged", this.setVolume)
      window.removeEventListener("stopAllAudioExcept", this.stopAudioExcept)
    }
  }

  stopAudioExcept = (event) => {
    const { sequence } = this.props
    if (event.detail.slugPlaying !== sequence.slug) {
      this.stopSound()
    }
  }

  setVolume = () => {
    const volume = getVolume()
    this.setState({ volumeValue: volume })
  }

  createPlay = () => {
    const { sequence } = this.props
    if (this.debouncedCreatePlay === undefined) {
      this.debouncedCreatePlay = _.debounce(sequence.createPlay, 1000, {leading: true, trailing: false})
    }

    this.debouncedCreatePlay()
  }

  playSound = (e) => {
    const { sequence } = this.props
    if (sequence.enabledForPlay === false) {
      console.log("Sample play disabled")
      return
    }

    this.setState({ shouldLoop: true })
    this.audioPlayer.current.play()
    this.createPlay()
    sequence.setToPlay()
    stopAllAudioExceptSlug(sequence.slug)
  }

  playSoundOnce = () => {
    const { sequence } = this.props
    if (sequence.enabledForPlay === false) {
      console.log("Sample play disabled")
      return
    }
    this.setState({
      shouldLoop: false,
    })
    this.createPlay()
    sequence.setToPlay()
    this.audioPlayer.current.play()
    stopAllAudioExceptSlug(sequence.slug)
  }

  stopSound = (e) => {
    const { sequence } = this.props
    this.setState({
      isPlaying: false,
    })
    sequence.setToStop()
    this.audioPlayer.current.stop()
  }

  onIsLoading = () => {
    this.setState({ isLoading: true })
  }

  onIsLoaded = () => {
    this.setState({
      isLoading: false,
      isLoaded: true
    })
  }

  onPlay = () => {
    this.setState({ isPlaying: true })
  }

  onFinishedPlaying = () => {
    const { sequence } = this.props
    sequence.setToStop()
    this.setState({ isPlaying: false })
  }

  render() {
    const {
      displayTitle,
      sequence,
      handleWaveformClick,
    } = this.props

    const {
      isPlaying,
      isLoading,
      shouldLoop,
      volumeValue,
    } = this.state

    return (
      <SequenceWaveformContainer
        onMouseOver={this.playSound}
        onMouseLeave={this.stopSound}
        onClick={handleWaveformClick}
      >
        <AudioPlayer
          url={sequence.sample_mp3_url || sequence.audioMp3Url}
          ref={this.audioPlayer}
          volume={volumeValue}
          shouldLoop={shouldLoop}
          onPlay={this.onPlay}
          onFinishedPlaying={this.onFinishedPlaying}
          onIsLoading={this.onIsLoading}
          onIsLoaded={this.onIsLoaded}
        />
        <CardImageContainer>

          <Fade in={isLoading}>
            <SFLinearProgress color="primary" />
          </Fade>
          <CardImage
            src={sequence.sequence_waveform_url || sequence.audioWaveformUrl}
            assignedColor={sequence.assigned_color}
          />
          {isPlaying && <CardPlayShadow duration={sequence.duration} />}
          {displayTitle && <WaveformTitle>{sequence.name}</WaveformTitle>}
        </CardImageContainer>
      </SequenceWaveformContainer>
    )
  }
}